import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, Typography, Box, Button, Avatar, CircularProgress } from '@mui/material';

import List from 'components/list/List';
import { DateField, LinkField, ListField } from 'components/list/Fields';
import ActionMenu from 'components/list/actionMenu/ActionMenu';
import BulkUpdate from 'components/forms/BulkUpdate';
import BulkDelete from 'components/forms/BulkDelete';
import { CheckCircleOutline, Draw, RemoveCircleOutline } from '@mui/icons-material';
import ActionMenuItemUpdate from 'components/list/actionMenu/ActionMenuItemUpdate';
import ActionMenuItemDelete from 'components/list/actionMenu/ActionMenuItemDelete';
import { ResourceContextProvider } from 'components/record/ResourceContext';

// Types for dashboard data
export interface DashboardEvent {
  slug: string;
  name: string;
  status: string;
  startDate: string;
  endDate: string;
  competitors?: Array<{ id: string }>;
}

export interface DashboardEntry {
  id: string;
  createdAt: string;
  user: {
    firstName: string;
    lastName: string;
  };
  event: {
    slug: string;
    name: string;
  };
}

// Simple Loading Component
export const LoadingSpinner = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
    <CircularProgress />
  </Box>
);

// Statistics Card Component
export const StatCard = ({
  title,
  value,
  icon,
  color = 'primary',
  subtitle
}: {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info';
  subtitle?: string;
}) => (
  <Card>
    <CardContent>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="h6" component="div" color={color}>
            {value}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Avatar sx={{ bgcolor: `${color}.main`, width: 48, height: 48 }}>{icon}</Avatar>
      </Box>
    </CardContent>
  </Card>
);

// Quick Action Card Component
export const QuickActionCard = ({
  title,
  description,
  icon,
  action,
  color = 'primary'
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
  action: () => void;
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info';
}) => (
  <Card sx={{ cursor: 'pointer', transition: '0.3s', '&:hover': { transform: 'translateY(-2px)' } }}>
    <CardContent onClick={action}>
      <Box display="flex" alignItems="center">
        <Avatar sx={{ bgcolor: `${color}.main`, mr: 2 }}>{icon}</Avatar>
        <Box>
          <Typography variant="h6" component="div">
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// Recent Events Component
export const RecentEvents = () => (
  <ResourceContextProvider resource="Event">
    <List
      bulkOptions={false}
      title="Recent Events"
      toolbar={
        <Box mt={2}>
          <Button component={Link} to="/my_events" variant="outlined" size="small">
            View All Events
          </Button>
        </Box>
      }
      hidePagination
      size={5}
    >
      <LinkField source="name" label="Event" link={(record) => `/my_events/${record?.slug}`} />
      <DateField source="startDate" label="Start Date" />
      <DateField source="endDate" label="End Date" />
      <ListField source="status" label="Status" />
    </List>
  </ResourceContextProvider>
);

// Waiting Entries Component
export const WaitingEntries = () => (
  <ResourceContextProvider resource="Entry">
    <List
      bulkOptions
      hidePagination
      title="Waiting Entries"
      avatar={<Draw />}
      refresh
      bulkActions={
        <Box gap={2} display="flex">
          <BulkUpdate data={{ status: 'accept' }} label="Accept" icon={<CheckCircleOutline />} />
          <BulkUpdate data={{ status: 'reject' }} label="Reject" icon={<RemoveCircleOutline />} />
          <BulkDelete />
        </Box>
      }
      filters={{ status: 'waiting' }}
    >
      <ListField source="userName" label="User Name" />
      {/* <DateField source="createdAt" label="Date" /> TODO: */}
      <ListField source="subEvent.event.name" label="Event" />
      <ListField source="subEvent.name" label="Sub Event" />
      <ListField source="status" label="Status" />
      <ActionMenu label="Actions" align="right" width="5%" variant="cell">
        <ActionMenuItemUpdate label="Accept" icon={<CheckCircleOutline />} data={{ status: 'accept' }} />
        <ActionMenuItemUpdate label="Reject" icon={<RemoveCircleOutline />} data={{ status: 'reject' }} />
        <ActionMenuItemDelete />
      </ActionMenu>
    </List>
  </ResourceContextProvider>
);
